// API service for Swipey.ai authentication endpoints
import { getRedTrackClickId } from "./redtrack";
import { getTrackingParams } from "./tracking";
import { setStorage, getStorage, setAuth } from "./storage";

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`;
const API_KEY = process.env.REACT_APP_API_KEY;

// Landing page source tag sent with auth requests (regular + Google)
export const LP_SOURCE = "swipeychat";

// Common headers for all API requests
const getHeaders = () => ({
  "Content-Type": "application/json",
  "x-api-key": API_KEY,
});

// ─── externalClickIds ─────────────────────────────────────────────────────────
//
// Mirrors the implementation in Match-LP / create-ai-slut / kawaii-slut /
// ai-pornub. The backend owns the list of click-id keys it wants forwarded
// (e.g. ['twclid','gclid','fbclid','ttclid']); we fetch that list once per
// page load and read each value from a sessionStorage snapshot of the
// landing-URL query — NOT from window.location at submit time (SPA navigation
// strips it), NOT from utm_data localStorage (leaks across visits). Failures
// fall back to {} so auth + OAuth never block.

const LANDING_URL_PARAMS_KEY = "landing_url_params";
const EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS = 3000;

let externalClickIdKeys = null;
let externalClickIdKeysPromise = null;

// Snapshot the URL query into sessionStorage on module load so click-ids stay
// available even after SPA navigation. Per-tab — sessionStorage clears with
// the tab close, so each fresh tab starts with its own snapshot.
(() => {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(LANDING_URL_PARAMS_KEY)) return;
    const snapshot = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      snapshot[key] = value;
    });
    sessionStorage.setItem(LANDING_URL_PARAMS_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore */
  }
})();

const fetchExternalClickIdKeys = async () => {
  if (Array.isArray(externalClickIdKeys)) return externalClickIdKeys;
  if (externalClickIdKeysPromise) return externalClickIdKeysPromise;

  externalClickIdKeysPromise = (async () => {
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), EXTERNAL_CLICK_ID_KEYS_TIMEOUT_MS)
      : null;
    try {
      const response = await fetch(
        `${API_BASE_URL}/marketing/external-click-id-keys`,
        {
          method: "GET",
          headers: getHeaders(),
          signal: controller?.signal,
        }
      );
      if (!response.ok) {
        externalClickIdKeys = [];
        return externalClickIdKeys;
      }
      const data = await response.json();
      externalClickIdKeys = Array.isArray(data) ? data : [];
      return externalClickIdKeys;
    } catch (err) {
      console.warn(
        "[externalClickIds] keys fetch failed, falling back to []",
        err
      );
      externalClickIdKeys = [];
      return externalClickIdKeys;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      externalClickIdKeysPromise = null;
    }
  })();

  return externalClickIdKeysPromise;
};

// Build the externalClickIds object the backend expects: for each key returned
// by /marketing/external-click-id-keys, read the value from this tab's
// landing-URL snapshot. Missing values come back as null.
export const getExternalClickIds = async () => {
  const keys = await fetchExternalClickIdKeys();
  let landingParams = {};
  try {
    const raw = sessionStorage.getItem(LANDING_URL_PARAMS_KEY);
    if (raw) landingParams = JSON.parse(raw) || {};
  } catch {
    landingParams = {};
  }
  return keys.reduce((acc, key) => {
    const value = landingParams[key];
    acc[key] = value == null || value === "" ? null : value;
    return acc;
  }, {});
};

// Generic API request function
const apiRequest = async (endpoint, method = "GET", body = null) => {
  try {
    const config = {
      method,
      headers: getHeaders(),
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// UTM data management - now using our tracking system
export const collectAndStoreUTMData = () => {
  const rtkClickID = document.cookie.match(/rtkclickid-store=([^;]+)/)?.[1];
  const trackingParams = getTrackingParams();

  const utmObj = {
    rtkcid: rtkClickID,
    click_id: rtkClickID,
    cmpid: rtkClickID,
    ...trackingParams,
    path: "/paywall",
  };

  // Store UTM data using our storage utility
  setStorage("utm_data", utmObj);
  return utmObj;
};

// Auth callback handler
export const handleAuthCallback = () => {
  const params = new URLSearchParams(window.location.search);
  const authToken = params.get("auth_token");
  const authType = params.get("auth_type");

  if (authToken && authType) {
    const utm = getStorage("utm_data") || {};
    const paywallUrl = new URL(`${SWIPEY_BASE_URL}/paywall`);

    paywallUrl.searchParams.set("auth_token", authToken);
    paywallUrl.searchParams.set("auth_type", authType);
    Object.entries(utm).forEach(([key, value]) => {
      if (value) paywallUrl.searchParams.set(key, value);
    });

    window.location.replace(paywallUrl.toString());
    return true; // Indicates callback was handled
  }
  return false; // No callback to handle
};

// Account creation API
export const createAccount = async (email, password, lp = null) => {
  const clickId = getRedTrackClickId();
  const payload = {
    email,
    password,
    clickId,
  };

  if (lp) {
    payload.lp = lp;
  }

  return apiRequest("/auth/account", "POST", payload);
};

// Pre-lander account creation API
export const createPreLanderAccount = async (email, password) => {
  const clickId = getRedTrackClickId();
  const utm = getStorage("utm_data") || {};
  const externalClickIds = await getExternalClickIds();

  const data = await apiRequest("/auth/pre-lander", "POST", {
    email,
    password,
    clickId,
    lp_source: LP_SOURCE,
    externalClickIds,
    ...utm,
  });

  if (
    data.message &&
    data.message.includes("User with this email already exists")
  ) {
    try {
      const loginData = await loginPreLander(email, password);

      if (loginData.success && loginData.authToken) {
        // Save auth data with authType for account creation
        setAuth(loginData.authToken, "user");

        const signInUrl = new URL(`${SWIPEY_BASE_URL}/sign_in`);
        signInUrl.searchParams.set("auth_token", loginData.authToken);
        signInUrl.searchParams.set("auth_type", "user");

        Object.entries(utm).forEach(([key, value]) => {
          if (value) signInUrl.searchParams.set(key, value);
        });

        return { success: false, redirectUrl: signInUrl.toString() };
      }
    } catch (err) {
      // Login attempt failed after existing user check
    }

    const signInUrl = new URL(`${SWIPEY_BASE_URL}/sign_in`);
    Object.entries(utm).forEach(([key, value]) => {
      if (value) signInUrl.searchParams.set(key, value);
    });
    return { success: false, redirectUrl: signInUrl.toString() };
  }

  return data;
};

// Pre-lander login API
export const loginPreLander = async (email, password) => {
  const clickId = getRedTrackClickId();
  const utm = getStorage("utm_data") || {};
  const externalClickIds = await getExternalClickIds();

  return apiRequest("/auth/pre-lander", "POST", {
    email,
    password,
    clickId,
    lp_source: LP_SOURCE,
    externalClickIds,
    ...utm,
  });
};

// Complete login flow handler
export const handleLoginFlow = async (email, password, currentPage) => {
  try {
    const clickId = getRedTrackClickId();
    const utm = getStorage("utm_data") || {};
    const externalClickIds = await getExternalClickIds();

    const authType =
      currentPage === "home" ? "login_pre_lander" : "login_paywall";

    const data = await apiRequest("/auth/pre-lander/login", "POST", {
      email,
      password,
      clickId,
      auth_type: authType,
      lp_source: LP_SOURCE,
      externalClickIds,
      ...utm,
    });

    const authToken = data.authToken || data.token;
    const user = data;

    if (authToken) {
      setAuth(authToken);

      const isSubscribed = user?.isSubscribed === true;

      if (isSubscribed) {
        const homeUrl = new URL(`${SWIPEY_BASE_URL}/`);
        homeUrl.searchParams.set("auth_token", authToken);
        homeUrl.searchParams.set("auth_type", "login_pre_lander");

        Object.entries(utm).forEach(([key, value]) => {
          if (value) homeUrl.searchParams.set(key, value);
        });

        return {
          success: true,
          redirectUrl: homeUrl.toString(),
        };
      } else {
        // User exists BUT is NOT subscribed -> redirect to paywall
        const paywallUrl = new URL(`${SWIPEY_BASE_URL}/paywall`);
        paywallUrl.searchParams.set("auth_token", authToken);
        paywallUrl.searchParams.set("auth_type", "login_paywall"); // додаємо auth_type у URL

        Object.entries(utm).forEach(([key, value]) => {
          if (value) paywallUrl.searchParams.set(key, value);
        });

        return {
          success: true,
          redirectUrl: paywallUrl.toString(),
        };
      }
    }

    return {
      success: false,
      message: "Please check your email or password",
    };
  } catch (error) {
    return {
      success: false,
      message: "Please check your email or password",
    };
  }
};

// Google auth URL
export const getGoogleAuthUrl = () =>
  `${SWIPEY_BASE_URL}/api/v1/auth/google/login`;

// Swipey base URL
export const getSwipeyBaseUrl = () => SWIPEY_BASE_URL;

// Export API configuration for debugging
export const getApiConfig = () => ({
  baseUrl: API_BASE_URL,
  swipeyBaseUrl: SWIPEY_BASE_URL,
  googleAuthUrl: getGoogleAuthUrl(),
  hasApiKey: !!API_KEY && API_KEY !== "your-api-key-here",
});

const API_BASE = `${API_BASE_URL}/auth`;

/**
 * Send reset password email
 * @param {string} email
 * @returns {Promise<void>}
 */
export const sendResetPasswordCode = async (email) => {
  try {
    const res = await fetch(`${API_BASE}/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || "Failed to send code");
    return data;
  } catch (err) {
    throw new Error(err.message || "Failed to send code");
  }
};

/**
 * Verify OTP code
 * @param {string} email
 * @param {string} otp
 * @returns {Promise<string>} token
 */
export const verifyResetPasswordOtp = async (email, otp) => {
  const res = await fetch(`${API_BASE}/verify-reset-password-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, otp }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "OTP verification failed");
  return data.token;
};

/**
 * Set new password
 * @param {string} token
 * @param {string} newPassword
 */
export const setNewPassword = async (token, newPassword) => {
  const res = await fetch(`${API_BASE}/set-new-password?token=${token}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "Password update failed");
};
